import { useState } from 'react';
import './FeedbackModal.css';
import { API_BASE } from '../config';

interface FeedbackModalProps {
  onClose: () => void;
  accessToken: string;
  userEmail?: string;
}

export function FeedbackModal({ onClose, accessToken, userEmail }: FeedbackModalProps) {
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) {
      setError('Please enter your feedback.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/v1/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ message: message.trim(), category, email: userEmail }),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error?.message || 'Failed to send feedback');
      }

      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="feedback-modal__overlay" onClick={onClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="feedback-title">
        <button type="button" className="feedback-modal__close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        {submitted ? (
          <div className="feedback-modal__success">
            <span className="feedback-modal__success-icon">🎉</span>
            <h2 id="feedback-title">Thanks for your feedback!</h2>
            <p>We've sent a copy to your email. We read every message and use it to make Hygh Vybe better.</p>
            <button type="button" className="feedback-modal__btn" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-modal__form">
            <h2 id="feedback-title">Give Us Feedback</h2>
            <p className="feedback-modal__subtitle">Help us improve Hygh Vybe. What's on your mind?</p>

            <label htmlFor="feedback-category" className="feedback-modal__label">Category</label>
            <select
              id="feedback-category"
              className="feedback-modal__select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="general">General feedback</option>
              <option value="bug">Bug report</option>
              <option value="feature">Feature request</option>
              <option value="product">Product suggestion</option>
              <option value="other">Other</option>
            </select>

            <label htmlFor="feedback-message" className="feedback-modal__label">Your feedback</label>
            <textarea
              id="feedback-message"
              className="feedback-modal__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think, report an issue, or suggest a feature..."
              rows={5}
              maxLength={2000}
            />
            <span className="feedback-modal__char-count">{message.length}/2000</span>

            {error && <p className="feedback-modal__error">{error}</p>}

            <button
              type="submit"
              className="feedback-modal__btn feedback-modal__btn--submit"
              disabled={submitting || !message.trim()}
            >
              {submitting ? 'Sending...' : 'Send Feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
